import clsx from "clsx";
import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  MAX_PAGE_OUTCOMES,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
} from "components/constants";
import { AddOutcomePlusButton } from "components/materials/page/outcomes/AddOutcomePlusButton";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { PageOutcomeScore } from "components/outcomes/PageOutcomeScore";
import { displayOutcomeName } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { TagIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  selectHasAssessmentsByPageId,
  selectIsEditModeEnabled,
  selectNestedPageOutcomes,
  selectOutcomeAssessmentsBySubmissionId,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";
import { isTestEnv } from "utils/env";
import { isPageDisabled } from "utils/page-utils";
import { useQueryParam, useQueryParamsActions } from "utils/routing";

type ExpandableOutcomesProps = {
  pageId: string;
};

export const ExpandableOutcomes: FC<ExpandableOutcomesProps> = ({ pageId }) => {
  const { setQueryParams, deleteQueryParams } = useQueryParamsActions();
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["assessments_published_at", "has_linked_submissions"],
  );
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const { canAuthorSection } = useCapabilities();
  const isAssessing = useIsAssessing();
  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, pageId));
  const hasAssessments = useAppSelector((s) => selectHasAssessmentsByPageId(s, pageId));
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const secondaryOverlay = useQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const outcomeAssessments = useListSelector(
    (s) => selectOutcomeAssessmentsBySubmissionId(s, submissionId),
    ["page_outcome_id", "score"],
  );
  const showAddOutcomeButton =
    canAuthorSection &&
    !hasAssessments &&
    pageOutcomes.length < MAX_PAGE_OUTCOMES &&
    isEditModeEnabled;
  const isCurrentPageDisabled = isPageDisabled(page);

  const openOutcomesSidebar = useCallback(() => {
    setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.Outcomes });
  }, [setQueryParams]);

  const toggleOutcomesSidebar = useCallback(() => {
    if (secondaryOverlay === SecondaryOverlay.Outcomes) {
      deleteQueryParams([SECONDARY_OVERLAY_QUERY_PARAM]);
    } else {
      setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.Outcomes });
    }
  }, [secondaryOverlay, deleteQueryParams, setQueryParams]);

  const [showShine, setShowShine] = useState(false);
  useEffect(() => {
    return addBroadcastListener(BROADCAST_TOPICS.AI_OUTCOMES_TAGGED, (message) => {
      if (message.pageId === pageId) {
        setShowShine(true);
        // Reset after animation completes
        setTimeout(() => setShowShine(false), 1500);
      }
    });
  }, [pageId]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [showShowMore, setShowShowMore] = useState(false);
  const [numberOfVisibleOutcomes, setNumberOfVisibleOutcomes] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateNumberOfVisibleOutcomes = useCallback(() => {
    if (!containerRef.current) return;

    // TODO: containerRef.current.offsetWidth is 0 in unit tests, using this workaround for now
    if (isTestEnv) {
      setNumberOfVisibleOutcomes(pageOutcomes.length);
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    let totalEstimatedWidthOfOutcomes = showAddOutcomeButton ? 34 + 12 : 0; // the add outcome button is 34px wide plus a 12px gap
    let numberOfOutcomesThatFitInOneLine = 0;
    let numberOfOutcomesThatFitInOneLineIncludingShowMoreButton = 0;

    for (const outcome of pageOutcomes) {
      const outcomeText = displayOutcomeName(outcome.outcome);
      // 7px is the average length of a character (note: this can be quite inaccurate depending on what letters are in the outcome text, but it's good enough for now)
      // 16px is the padding for each outcome
      // 12px is the gap between outcomes
      const outcomeWidth = outcomeText.length * 7 + 16 + 12;
      totalEstimatedWidthOfOutcomes += outcomeWidth;
      if (totalEstimatedWidthOfOutcomes < containerWidth) {
        numberOfOutcomesThatFitInOneLine += 1;
      }
      // 94px is the width of the show more button
      if (totalEstimatedWidthOfOutcomes + 94 < containerWidth) {
        numberOfOutcomesThatFitInOneLineIncludingShowMoreButton += 1;
      }
    }

    if (numberOfOutcomesThatFitInOneLine === pageOutcomes.length) {
      setNumberOfVisibleOutcomes(pageOutcomes.length);
      setShowShowMore(false);
      setIsExpanded(false);
    } else {
      setNumberOfVisibleOutcomes(numberOfOutcomesThatFitInOneLineIncludingShowMoreButton);
      setShowShowMore(true);
    }
  }, [showAddOutcomeButton, pageOutcomes]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(calculateNumberOfVisibleOutcomes);
    observer.observe(containerRef.current);
    calculateNumberOfVisibleOutcomes();

    return () => observer.disconnect();
  }, [calculateNumberOfVisibleOutcomes]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="relative overflow-hidden" ref={containerRef}>
      <div
        className={clsx(
          "animate-shine-base",
          showShine && "animate-shine-active",
          !isAssessing && "flex flex-wrap items-center gap-3",
        )}
      >
        {pageOutcomes
          .slice(0, isExpanded ? pageOutcomes.length : numberOfVisibleOutcomes)
          .map((pageOutcome) => (
            <PageOutcomeScore
              key={pageOutcome.id}
              outcomeAssessment={
                page?.assessments_published_at
                  ? outcomeAssessments.find((oa) => oa.page_outcome_id === pageOutcome.id)
                  : undefined
              }
              pageOutcome={pageOutcome}
              onClick={openOutcomesSidebar}
            />
          ))}

        {showAddOutcomeButton &&
          (pageOutcomes.length === 0 ? (
            <SquareButton
              disabled={isCurrentPageDisabled}
              kind="tertiary"
              size="xs"
              onClick={toggleOutcomesSidebar}
            >
              <IconText iconStart={<TagIcon />} text={t("glossary.outcomes")} />
            </SquareButton>
          ) : (
            <AddOutcomePlusButton
              iconButtonSize="xs"
              isDisabled={hasAssessments || isCurrentPageDisabled}
              pageId={pageId}
              title={t("common.add.outcome")}
            />
          ))}

        {showShowMore && !isExpanded && (
          <div className="">
            <SquareButton kind="tertiary" size="xs" onClick={toggleExpanded}>
              <span>{t("expandable_text.show_more")}</span>
            </SquareButton>
          </div>
        )}

        {isExpanded && (
          <div className="">
            <SquareButton kind="tertiary" size="xs" onClick={toggleExpanded}>
              <span>{t("expandable_text.show_less")}</span>
            </SquareButton>
          </div>
        )}
      </div>
    </div>
  );
};
